import PhoneInput from "react-phone-input-2";
import { InputWrapper } from "../Wrapper/InputBase";
import "./css/phoneInput.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { colors } from "common/const/colors";
import { useController, useFormContext } from "react-hook-form";
import { alpha3ToAlpha2 } from "i18n-iso-countries";

export const CustomPhoneInput = ({
  // InputWrapper
  label,
  placeholder,
  caption,
  required,

  // Input
  codeFieldName,
  numberFieldName,
  onChange,
  disabled,

  // 기본 선택 국가 (alpha3, 예: "KOR"). 없으면 localStorage("country") 사용
  defaultCountryCode,
}) => {
  if (!codeFieldName || !numberFieldName) {
    throw new Error(
      "CustomPhoneInput: codeFieldName, numberFieldName is Empty",
    );
  }

  const { control } = useFormContext();

  // 국가 코드 필드
  const codeController = useController({
    name: codeFieldName,
    control,
    rules: {
      validate: (value) => {
        if (required && (!value || String(value).trim() === "")) {
          return false;
        }
        return true;
      },
    },
  });

  // 전화번호 필드
  const numberController = useController({
    name: numberFieldName,
    control,
    rules: {
      validate: (value) => {
        if (required && (!value || String(value).trim() === "")) {
          return false;
        }
        return true;
      },
    },
  });

  const phoneCode = codeController.field.value ?? "";
  const phoneNumber = numberController.field.value ?? "";
  const error =
    numberController.fieldState.error || codeController.fieldState.error;

  const [dropdownStyle, setDropdownStyle] = useState({});
  const wrapperRef = useRef(null);

  // 기본 선택 국가(alpha2). defaultCountryCode > localStorage 순으로 결정한다.
  const defaultCountry = useMemo(() => {
    // ComboBox 값이 객체({ value })로 들어올 수도 있어 문자열로 정규화한다.
    const alpha3 =
      (typeof defaultCountryCode === "object"
        ? defaultCountryCode?.value
        : defaultCountryCode) || localStorage.getItem("country");

    return alpha3ToAlpha2(alpha3)?.toLowerCase();
  }, [defaultCountryCode]);

  // 로컬 번호 입력 전에는 기본 국가에 따라 remount하고, 입력 후에는 key를 고정한다.
  const computeInputKey = () =>
    phoneNumber ? "has-number" : `default-${defaultCountry ?? "none"}`;

  // input이 포커스된 동안에는 key를 바꾸지 않아 remount(포커스 손실)를 막는다.
  const isFocusedRef = useRef(false);
  const prevDefaultRef = useRef(defaultCountry);
  const [inputKey, setInputKey] = useState(computeInputKey);

  useEffect(() => {
    // phone input에 포커스가 있는 동안에는 remount를 미룬다.
    if (isFocusedRef.current) return;

    const defaultChanged = prevDefaultRef.current !== defaultCountry;
    prevDefaultRef.current = defaultCountry;

    // 기본 국가가 바뀌었고 아직 사용자가 번호를 입력하지 않았다면,
    // 코드를 비워 remount 후 onMount가 새 국가코드로 다시 채우도록 한다.
    if (defaultChanged && !phoneNumber) {
      if (onChange) {
        onChange({ code: "", number: "" });
      } else {
        codeController.field.onChange("");
      }
    }

    setInputKey(computeInputKey());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneNumber, defaultCountry]);

  const handleOpen = () => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setDropdownStyle({
        top: rect.bottom + window.scrollY,
        left: rect.left,
        width: rect.width,
        zIndex: 10,
      });
    }
  };

  useEffect(() => {
    const flagButton = wrapperRef.current?.querySelector(".selected-flag");
    if (flagButton) {
      flagButton.addEventListener("click", handleOpen);
      return () => flagButton.removeEventListener("click", handleOpen);
    }
  }, []);

  useEffect(() => {
    const countryList = wrapperRef.current?.querySelector(".country-list");
    if (!countryList) return;

    // MutationObserver로 드롭다운 열림/닫힘 감지
    const observer = new MutationObserver(() => {
      const isOpen = !countryList.classList.contains("hide");

      if (isOpen) {
        // 드롭다운이 열렸을 때
        const rect = wrapperRef.current.getBoundingClientRect();
        countryList.style.position = "fixed";
        countryList.style.top = `${rect.bottom}px`;
        countryList.style.left = `${rect.left}px`;
        countryList.style.width = `${rect.width}px`;
        countryList.style.zIndex = "10";
      }
    });

    observer.observe(countryList, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  // 스크롤 시 위치 업데이트
  useEffect(() => {
    const updatePosition = () => {
      const countryList = wrapperRef.current?.querySelector(".country-list");

      if (countryList && !countryList.classList.contains("hide")) {
        const rect = wrapperRef.current.getBoundingClientRect();
        countryList.style.top = `${rect.bottom}px`;
        countryList.style.left = `${rect.left}px`;
      }
    };

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, []);

  const handlePhoneChange = (newValue, data) => {
    const localNumber = newValue.substring(data.dialCode?.length);
    const info = { code: data.dialCode, number: localNumber };

    if (onChange) {
      onChange(info);
    } else {
      // useController의 onChange를 직접 호출 → form state 갱신 + 검증 자동
      codeController.field.onChange(info.code);
      numberController.field.onChange(info.number);
    }
  };

  // 마운트 시 사용자가 번호를 입력하지 않았으면 기본 국가의 국가코드를 채운다.
  const handleMount = (value, data) => {
    if (phoneNumber || !data?.dialCode) return;

    const info = { code: data.dialCode, number: "" };
    if (onChange) {
      onChange(info);
    } else {
      codeController.field.onChange(info.code);
    }
  };

  const handleFocus = () => {
    isFocusedRef.current = true;
  };

  const handleBlur = () => {
    isFocusedRef.current = false;
    // 포커스가 빠졌을 때(입력 종료) 지연됐던 key 상태를 반영한다.
    setInputKey(computeInputKey());
    codeController.field.onBlur();
    numberController.field.onBlur();
  };

  return (
    <InputWrapper
      ref={wrapperRef}
      label={label}
      caption={caption}
      rules={{ required }}
      error={error}>
      <PhoneInput
        key={inputKey}
        onMount={handleMount}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handlePhoneChange}
        inputStyle={{
          borderColor: error ? colors.Red : colors.Gray,
        }}
        dropdownStyle={dropdownStyle}
        disabled={disabled}
        value={String(phoneCode + phoneNumber || "")}
        country={!phoneNumber ? defaultCountry : undefined}
        placeholder={placeholder}
        // RHF가 검증 실패 시 이 input으로 focus(→ 스크롤)할 수 있도록 ref 연결
        inputProps={{
          ref: (el) => {
            codeController.field.ref(el);
            numberController.field.ref(el);
          },
        }}
      />
    </InputWrapper>
  );
};
